import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, ArrowUp, Hash, Lock, Volume2 } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { MessageItem } from './MessageItem';
import { MessageComposer } from './MessageComposer';
import { ChannelCallBanner } from '../calling/ChannelCallBanner';

/** Time threshold (ms) to break a message group even from the same sender */
const GROUP_BREAK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export const ChatArea: React.FC = () => {
  const [searchParams] = useSearchParams();

  const targetMessageId = searchParams.get('messageId');
  const targetThreadId = searchParams.get('threadId');

  const {
    activeChat,
    messages,
    isLoadingMessages,
    hasMoreMessages,
    loadOlderMessages,
    sendMessage,
    openThread,
  } = useChat();

  const { user } = useAuth();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef<boolean>(true);
  const prevChatIdRef = useRef<string | null>(null);
  const prevMessagesCountRef = useRef<number>(0);

  const activeChatId = activeChat
    ? activeChat.type === 'channel'
      ? activeChat.channel.id
      : activeChat.conversation.id
    : null;

  // Instant scroll to bottom without visible animation
  const scrollToBottomInstant = useCallback(() => {
    const el = scrollContainerRef.current;

    if (el) {
      el.scrollTop = el.scrollHeight;
      isNearBottomRef.current = true;
    }
  }, []);

  // Track if user has manually scrolled away from the bottom
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;

    if (!el) return;

    const threshold = 120;

    const distanceFromBottom =
      el.scrollHeight -
      el.scrollTop -
      el.clientHeight;

    isNearBottomRef.current = distanceFromBottom <= threshold;
  }, []);

  /**
   * Instant positioning before paint on:
   * - chat switch
   * - initial message load
   * - new messages
   *
   * Only auto-scrolls new messages when the user was already
   * near the bottom.
   */
  useLayoutEffect(() => {
    if (!activeChatId) return;

    const isChatSwitched =
      prevChatIdRef.current !== activeChatId;

    if (isChatSwitched) {
      prevChatIdRef.current = activeChatId;
      prevMessagesCountRef.current = messages.length;
      isNearBottomRef.current = true;

      scrollToBottomInstant();

      requestAnimationFrame(() => {
        scrollToBottomInstant();
      });

      return;
    }

    // New message arrived in current chat.
    // Only scroll if the user was already near the bottom.
    if (messages.length > prevMessagesCountRef.current) {
      if (isNearBottomRef.current) {
        scrollToBottomInstant();
      }
    }

    prevMessagesCountRef.current = messages.length;
  }, [
    activeChatId,
    messages.length,
    scrollToBottomInstant,
  ]);

  /**
   * Deep-link scroll to a specific message.
   *
   * If ?messageId=... exists, scroll to that message.
   *
   * We intentionally do not use smooth scrolling here when switching
   * chats because the normal chat positioning logic already handles
   * initial placement. The target message should take priority.
   */
  useEffect(() => {
    if (!targetMessageId) return;

    const targetElement = document.getElementById(
      `msg-${targetMessageId}`
    );

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [messages.length, targetMessageId]);

  /**
   * Deep-link auto-open thread.
   *
   * If ?threadId=... exists and the parent message is available,
   * automatically open its thread.
   */
  useEffect(() => {
    if (!targetThreadId || messages.length === 0) {
      return;
    }

    const parentMsg = messages.find(
      message => message.id === targetThreadId
    );

    if (parentMsg) {
      openThread(parentMsg as any);
    }
  }, [targetThreadId, messages, openThread]);

  /**
   * Keep the user at the bottom if content height changes while
   * they are already near the bottom.
   *
   * This is important for:
   * - images loading
   * - code blocks rendering
   * - lazy content
   * - message attachments changing height
   */
  useEffect(() => {
    const el = scrollContainerRef.current;

    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      if (isNearBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!activeChat) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
        <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mb-4 text-primary">
          <MessageSquare className="h-8 w-8" />
        </div>

        <h3 className="font-bold text-lg text-foreground mb-1">
          Welcome to your Workspace
        </h3>

        <p className="text-sm max-w-sm">
          Select a channel or direct message from the sidebar to start collaborating.
        </p>
      </div>
    );
  }

  const isChannel = activeChat.type === 'channel';

  const otherMember = !isChannel
    ? (
        activeChat.conversation?.members?.find(
          m => m.id !== user?.id
        ) || activeChat.conversation?.members?.[0]
      )
    : null;

  const chatName = isChannel
    ? activeChat.channel?.name || 'Channel'
    : otherMember?.displayName || 'Direct Message';

  const Icon = isChannel
    ? (
        activeChat.channel.type === 'ANNOUNCEMENT'
          ? Volume2
          : activeChat.channel.isPrivate
            ? Lock
            : Hash
      )
    : MessageSquare;

  // Compute message grouping metadata
  const getGroupingInfo = (index: number) => {
    const msg = messages[index];

    if (!msg) {
      return {
        isFirstInGroup: true,
        isLastInGroup: true,
      };
    }

    const prevMsg =
      index > 0
        ? messages[index - 1]
        : undefined;

    const nextMsg =
      index < messages.length - 1
        ? messages[index + 1]
        : undefined;

    const isSameSenderAsPrev =
      !!prevMsg &&
      prevMsg.sender.id === msg.sender.id &&
      !prevMsg.deletedAt &&
      !msg.deletedAt &&
      (
        new Date(msg.createdAt).getTime() -
        new Date(prevMsg.createdAt).getTime()
      ) < GROUP_BREAK_THRESHOLD_MS;

    const isSameSenderAsNext =
      !!nextMsg &&
      nextMsg.sender.id === msg.sender.id &&
      !nextMsg.deletedAt &&
      !msg.deletedAt &&
      (
        new Date(nextMsg.createdAt).getTime() -
        new Date(msg.createdAt).getTime()
      ) < GROUP_BREAK_THRESHOLD_MS;

    return {
      isFirstInGroup: !isSameSenderAsPrev,
      isLastInGroup: !isSameSenderAsNext,
    };
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-background relative">
      {/* Active Channel Call Banner */}
      <ChannelCallBanner />

      {/* Message List */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-2"
      >
        {/* Inner wrapper — pushes messages to the bottom when few */}
        <div className="flex flex-col justify-end min-h-full">

          {/* Load older messages button */}
          {hasMoreMessages && (
            <div className="flex justify-center py-2">
              <button
                onClick={loadOlderMessages}
                disabled={isLoadingMessages}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-muted hover:bg-muted/80 text-muted-foreground text-xs font-semibold rounded-full border border-border transition"
              >
                <ArrowUp className="h-3.5 w-3.5" />
                <span>Load older messages</span>
              </button>
            </div>
          )}

          {/* Conversation start indicator */}
          {isChannel ? (
            /* Channel: keep the existing rich welcome banner */
            <div className="p-6 mb-4 rounded-2xl bg-card border border-border/80 text-card-foreground">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                <Icon className="h-6 w-6" />
              </div>

              <h2 className="text-xl font-bold tracking-tight mb-1">
                {`Welcome to #${chatName}!`}
              </h2>

              <p className="text-xs text-muted-foreground">
                {`This is the start of the #${chatName} channel. Start the conversation!`}
              </p>
            </div>
          ) : (
            /* DM: "Today" pill divider matching target design */
            <div className="flex items-center gap-3 py-6 px-4">
              <div className="flex-1 h-px bg-border" />

              <span className="text-xs text-muted-foreground font-medium px-3 py-1 bg-card border border-border rounded-full">
                Today
              </span>

              <div className="flex-1 h-px bg-border" />
            </div>
          )}

          {/* Messages with grouping */}
          <div>
            {messages.map((message, index) => {
              const {
                isFirstInGroup,
                isLastInGroup,
              } = getGroupingInfo(index);

              return (
                <MessageItem
                  key={message.id}
                  message={message}
                  isFirstInGroup={isFirstInGroup}
                  isLastInGroup={isLastInGroup}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Message Composer */}
      <MessageComposer
        onSendMessage={sendMessage}
        placeholder={
          isChannel
            ? `Message #${chatName}`
            : `Message ${chatName}`
        }
      />
    </div>
  );
};